import React from 'react';
import styled, { keyframes } from 'styled-components';
import { device } from '../../styles/devices';
import { color, gutter } from '../../styles/mixins';
import { caps12, label16, m20, p14 } from '../../styles/typography';
import { FormatAmount } from '../format-amount/format-amount';
import { Skeleton, SkeletonHost } from '../skeleton/skeleton';
import { LotTimer } from './lot-timer';

const loadIn = keyframes`
  0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
`;

const Host = styled.div`
  @media ${device.tablet} {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
`;

const Image = styled.figure`
  aspect-ratio: 1;
  margin: 0 auto;
  margin-bottom: ${gutter(2)};
  align-self: center;
  flex: 1;

  @media ${device.tablet} {
    aspect-ratio: 469 / 436;
    padding-right: 24px;
  }

  @media ${device.desktop} {
    aspect-ratio: 636 / 476;
  }

  ${SkeletonHost} {
    height: 100%;
    width: 100%;
  }

  > img {
    width: 100%;
    height: 100%;
    animation: 0.3s ease-in ${loadIn};
  }
`;
const BrandName = styled.h2`
  ${caps12};

  margin-bottom: ${gutter(1)};
`;

const Background = styled.div`
  padding: ${gutter(3)} ${gutter(2)};
  margin: 0 -${gutter(2)};
  background-color: ${color('gray10')};

  @media ${device.mobile} {
    padding: ${gutter(3)} ${gutter(4)};
    margin: 0 -${gutter(4)};
  }

  @media ${device.tablet} {
    margin: 0;
    width: 422px;
    flex-shrink: 0;
  }
`;

const Status = styled.div`
  margin-bottom: ${gutter(1.2)};
`;

const Title = styled.p`
  ${m20};
  margin-bottom: ${gutter(1)};
`;

const Summary = styled.p`
  ${p14};
`;

const Estimates = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: ${(props) => `1px solid ${props.theme.border.color}`};
  padding: 22px 0;
  margin-bottom: ${gutter(2.5)};
`;

const Bid = styled.div`
  ${label16}
  display: flex;
  justify-content: space-between;
  margin-bottom: ${gutter(2.5)};
`;

const Actions = styled.div`
  > * {
    margin-bottom: ${gutter(2)};

    &:last-child {
      margin-bottom: 0;
    }
  }

  ${SkeletonHost} {
    width: 100%;
  }
`;

interface LotDetailsProps {
  isLoading?: boolean;
  image?: React.ReactNode;
  status?: React.ReactNode;
  brandName?: string;
  title?: string | null;
  summary?: string | null;
  highestBid?: number | null;
  actions?: React.ReactNode;
  startsAt?: string;
  endsAt?: string;
  cardOnly?: boolean;
  buyersPremiumPercentage?: number;
  overheadPremiumPercentage?: number;
  isLotClosed?: boolean;
}

export const LotDetails = ({
  isLoading,
  image,
  status,
  brandName,
  title,
  summary,
  highestBid,
  actions,
  startsAt,
  endsAt,
  cardOnly,
  isLotClosed = false,
  buyersPremiumPercentage,
  overheadPremiumPercentage,
}: LotDetailsProps) => {
  return (
    <Host>
      {!cardOnly && (
        <Image>
          {isLoading ? <Skeleton width={500} height={500} visible /> : image}
        </Image>
      )}
      <Background>
        <LotTimer startsAt={startsAt} endsAt={endsAt} isLoading={isLoading} />
        {status && <Status>{status}</Status>}
        <BrandName>
          {isLoading ? <Skeleton width={230} height={21} visible /> : brandName}
        </BrandName>
        <Title>
          {isLoading ? <Skeleton width={230} height={26} visible /> : title}
        </Title>
        <Summary>
          {isLoading ? <Skeleton width={300} height={25} visible /> : summary}
        </Summary>
        <Estimates>
          <span>Estimates upon request</span>
        </Estimates>
        <Bid>
          {isLoading ? (
            <Skeleton width={103} height={27} visible />
          ) : (
            <>
              <span>Highest bid:</span>{' '}
              {highestBid ? (
                <FormatAmount
                  value={highestBid}
                  buyersPremiumPercentage={
                    isLotClosed ? buyersPremiumPercentage : undefined
                  }
                  overheadPremiumPercentage={
                    isLotClosed ? overheadPremiumPercentage : undefined
                  }
                />
              ) : (
                'No bids yet'
              )}
            </>
          )}
        </Bid>
        <Actions>
          {isLoading ? (
            <>
              <Skeleton width={103} height={43} visible />
              <Skeleton width={103} height={43} visible />
            </>
          ) : (
            actions
          )}
        </Actions>
      </Background>
    </Host>
  );
};
